/* GrowthOS — Attachments (shared across all module view pages).
   Ported from design growthos/Attachments. Wired to the real attachment APIs
   (uploadFilesCRM + createAttachment to add, downloadFileCRM to download,
   deleteAttachment to remove) and the redux attachment list on `localData.attachments`. */
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../../services/service';
import { fetchAttachmentsAndAppendToModule } from '../../../redux/actions';
import { DetailDateTimeFormatter, NameFormatter } from '../../../shared/controls/formatters';
import { SizeConverter } from '../../../shared/controls/formatters/SizeConverter';

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

const ICONS = {
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  upload: <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />,
  download: <path d="M12 3v12M7 12l5 5 5-5M5 21h14" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  file: (<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>),
  sheet: (<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M8 13h8M8 17h8M12 13v4" /></>),
  slides: (<><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M12 17v3M8 20h8" /></>),
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  archive: (<><rect x="3" y="4" width="18" height="5" rx="1" /><path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9M10 13h4" /></>),
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const EXT_CFG = {
  pdf: { tone: 'red', icon: 'file' },
  doc: { tone: 'blue', icon: 'file' }, docx: { tone: 'blue', icon: 'file' }, txt: { tone: 'blue', icon: 'file' },
  xls: { tone: 'green', icon: 'sheet' }, xlsx: { tone: 'green', icon: 'sheet' }, csv: { tone: 'green', icon: 'sheet' },
  ppt: { tone: 'amber', icon: 'slides' }, pptx: { tone: 'amber', icon: 'slides' },
  png: { tone: 'purple', icon: 'image' }, jpg: { tone: 'purple', icon: 'image' }, jpeg: { tone: 'purple', icon: 'image' },
  gif: { tone: 'purple', icon: 'image' }, webp: { tone: 'purple', icon: 'image' }, svg: { tone: 'purple', icon: 'image' },
  zip: { tone: 'gray', icon: 'archive' }, rar: { tone: 'gray', icon: 'archive' }, '7z': { tone: 'gray', icon: 'archive' },
};
const extOf = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || ''));
  return m ? m[1].toLowerCase() : '';
};

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const FileTypeIcon = ({ ext }) => {
  const cfg = EXT_CFG[ext] || { tone: 'neutral', icon: 'file' };
  return (
    <div className={`gos-att-fic tone-${cfg.tone}`}>
      <Ic name={cfg.icon} size={15} />
      {ext ? <span className="gos-att-fic-ext">{ext}</span> : null}
    </div>
  );
};

const Attachments = ({ localData, module = 'Lead' }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const files = localData?.attachments || [];
  const totalBytes = files.reduce((s, f) => s + (Number(f.size) || 0), 0);

  const refresh = () =>
    dispatch(fetchAttachmentsAndAppendToModule({ moduleName: module, id: localData?.id }));

  const upload = async (fileList) => {
    if (!localData?.id) return;
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const tooBig = picked.filter((f) => f.size > MAX_BYTES);
    const ok = picked.filter((f) => f.size <= MAX_BYTES);
    tooBig.forEach((f) => toast.error(`${f.name} exceeds the 25 MB limit.`, { position: 'top-right' }));
    if (!ok.length) return;

    setBusy(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    try {
      const processed = [];
      for (const file of ok) {
        const newFileName = `${localData.id}_${timestamp}_${file.name}`;
        processed.push(new File([file], newFileName, { type: file.type }));
        // eslint-disable-next-line no-await-in-loop
        await apiServices.createAttachment({
          fileName: file.name,
          cloudStorageURL: newFileName,
          size: `${file.size}`,
          module,
          moduleId: localData.id,
        });
      }
      const uploaded = await apiServices.uploadFilesCRM(processed, 'Attachments');
      if (uploaded === false) throw new Error('upload failed');
      refresh();
      toast.success(`${ok.length} file${ok.length > 1 ? 's' : ''} uploaded.`, { position: 'top-right' });
    } catch (err) {
      toast.error('File upload failed.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    upload(e.target.files);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files);
  };

  const download = (f) => apiServices.downloadFileCRM(f.cloudStorageURL, 'Attachments', f.fileName);

  const remove = (f) => {
    if (busy) return;
    setBusy(true);
    apiServices
      .deleteAttachment({ id: f.id, module, moduleId: localData?.id })
      .then((response) => {
        if (response.status === 200) {
          refresh();
          toast.success('Attachment deleted.', { position: 'top-right' });
        } else {
          toast.error('Could not delete the attachment.', { position: 'top-right' });
        }
      })
      .catch(() => toast.error('Could not delete the attachment.', { position: 'top-right' }))
      .finally(() => setBusy(false));
  };

  return (
    <div className="gos-lr-card gos-att">
      <input ref={inputRef} type="file" multiple hidden onChange={onPick} />

      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="paperclip" size={16} /> Attachments
          {files.length > 0 ? <span className="gos-lr-count">{files.length}</span> : null}
        </div>
        <button type="button" className="gos-att-upload" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <Ic name="upload" size={14} />
          )}
          Upload File
        </button>
      </div>

      {/* drop zone */}
      <label
        className={'gos-att-drop' + (dragging ? ' dragging' : '')}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input type="file" multiple hidden onChange={onPick} />
        <span className="gos-att-drop-ic">
          <Ic name="upload" size={16} />
        </span>
        <div>
          <div className="gos-att-drop-title">
            {dragging ? 'Release to upload' : 'Drop files here, or click to browse'}
          </div>
          <div className="gos-att-drop-sub">PDF, DOCX, XLSX, PNG, JPG — max 25 MB each</div>
        </div>
      </label>

      {/* list / empty */}
      {files.length > 0 ? (
        <div className="gos-att-list">
          {files.map((f, i) => {
            const ext = extOf(f.fileName);
            const uploader = NameFormatter(f.createdBy) || 'Unknown';
            return (
              <div className="gos-att-row" key={f.id || i}>
                <FileTypeIcon ext={ext} />
                <div className="gos-att-main">
                  <div className="gos-att-name" onClick={() => download(f)} title={f.fileName}>
                    {f.fileName}
                  </div>
                  <div className="gos-att-meta">
                    <span className="gos-att-ava" style={{ background: avaColor(uploader) }}>
                      {initials(uploader)}
                    </span>
                    <span className="gos-att-uploader">{uploader}</span>
                    {f.createdDateTime ? (
                      <>
                        <span className="gos-att-dot">·</span>
                        <span className="gos-att-date">{DetailDateTimeFormatter(f.createdDateTime)}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <span className="gos-att-size">{f.size ? SizeConverter(f.size) : ''}</span>
                <div className="gos-att-actions">
                  <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" title="Download" onClick={() => download(f)}>
                    <Ic name="download" size={15} />
                  </button>
                  <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm gos-att-del" title="Delete" onClick={() => remove(f)} disabled={busy}>
                    <Ic name="trash" size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="gos-att-empty">
          <div className="gos-att-empty-ic">
            <Ic name="paperclip" size={21} />
          </div>
          <div className="gos-att-empty-title">No files attached</div>
          <div className="gos-att-empty-text">
            Upload contracts, proposals, call recordings, or any relevant documents.
          </div>
        </div>
      )}

      {files.length > 0 ? (
        <div className="gos-att-foot">
          <Ic name="paperclip" size={13} />
          <span>
            {files.length} file{files.length !== 1 ? 's' : ''}
            {totalBytes ? ` · ${SizeConverter(totalBytes)} total` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default Attachments;
